import queryString from 'utils/queryString';

import CountryCodeCookie from '../lib/countryCodeCookie';
import nextApiGetRequest from '../lib/nextApiGetRequest';

const dataApi = {
  /**
   * Fetch redis-cached list of available SKUs
   * @returns {{ data: Array, error: object }}
   */
  async getSkuProducts() {
    try {
      const response = await nextApiGetRequest('/api/products');

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e };
    }
  },

  /**
   * Fetch redis-cached list of supported countries
   * @returns {{ data: Array, error: object }}
   */
  async getCountries() {
    try {
      const response = await nextApiGetRequest('/api/countries');

      const json = await response.json();

      const data = json.data;

      if (data.length > 0) {
        return { data };
      }
      return { error: { code: 500, body: 'No countries available' } };
    } catch (e) {
      return { error: e };
    }
  },

  /**
   * Fetch actual app status
   * @returns {{ data: object, error: object }}
   */
  async getAppStatus() {
    try {
      const response = await nextApiGetRequest('/api/status');

      const json = await response.json();

      return { data: json };
    } catch (e) {
      return { error: e };
    }
  },

  /**
   * Fetch redis-cached list of shippings
   * @returns {{ data: object, error: object }}
   */
  async getShippings() {
    try {
      const response = await nextApiGetRequest('/api/shippings');

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e };
    }
  },

  /**
   * Fetch redis-cached template (backend proxy template)
   * @param {string} productId
   * @param {string} version
   * @returns {{ data: object, error: object }}
   */
  async getTemplate(productId, version) {
    try {
      const response = await nextApiGetRequest(
        queryString.stringifyUrl('/api/templates', {
          product_id: productId,
          version,
        }),
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e };
    }
  },

  /**
   * Fetch redis-cached templates (backend proxy templates)
   * @param {Array<{ productId: string, version: string }>} products
   * @returns {{ data: Array, error: object }}
   */
  async getVersionedTemplates(products = []) {
    try {
      const response = await nextApiGetRequest(
        queryString.stringifyUrl('/api/templates', {
          products: products.map(({ productId, version }) => `${productId}:${version}`).join(','),
        }),
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e };
    }
  },

  /**
   * Get current countryCode
   * @param {object} ctx NextJS Context
   * @param {string} defaultCountryCode defaultCountryCode
   * @returns {object}
   */
  async getGeo(ctx = null, defaultCountryCode) {
    let realCountryCode = CountryCodeCookie.get(ctx);
    if (!realCountryCode) {
      let url = `/api/geo`;
      if (ctx && ctx.req) {
        const xForwardedFor = (ctx.req.headers['x-forwarded-for'] || '').replace(/:\d+$/, '');
        const ip = xForwardedFor || ctx.req.connection.remoteAddress;

        url = queryString.stringifyUrl(url, {
          ip,
        });
      }

      try {
        const response = await nextApiGetRequest(url);

        const json = await response.json();

        // A 200 response can still carry no countryCode (geo lookup miss,
        // e.g. datacenter IPs) — fall back instead of caching undefined.
        realCountryCode = json.data?.countryCode;
        if (realCountryCode) {
          CountryCodeCookie.set(realCountryCode, ctx);
        } else {
          realCountryCode = defaultCountryCode;
        }
      } catch (e) {
        realCountryCode = defaultCountryCode;
      }
    }

    return {
      countryCode: defaultCountryCode,
      realCountryCode,
    };
  },
};

export default dataApi;
